import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  X,
  Sparkles,
  Loader2,
  Crown,
  Lightbulb,
  ListChecks,
  Calendar,
  BrainCircuit,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Bookmark,
  BookmarkCheck,
} from "lucide-react";
import { toast } from "sonner";

/**
 * CoachToolModal — shared modal for the 4 AI Study Coach power tools:
 *   - explain        (question, student_answer, correct_answer)
 *   - step-by-step   (problem)
 *   - quiz           (subject, topic, difficulty, count)
 *   - exam-plan      (subject, topics, days, hours_per_day)
 *
 * Pro-only on the backend. If the API returns 402 pro_only we render an
 * inline "Upgrade to Pro" card instead of the tool UI.
 *
 * Props:
 *   open (bool)
 *   onClose (fn)
 *   type ('explain' | 'step' | 'quiz' | 'exam')
 *   defaults (object) — seed values for the form (see per-type shape below)
 */
const TOOL_META = {
  explain: {
    icon: Lightbulb,
    label: "AI Explain this answer",
    endpoint: "/coach/explain-answer",
    method: "post",
    desc: "Gemini 2.5 Flash unpacks why the correct answer is correct, why your answer wasn't, and gives a memory hook.",
  },
  step: {
    icon: BrainCircuit,
    label: "AI Step-by-step solution",
    endpoint: "/coach/step-by-step",
    method: "post",
    desc: "Walks through the problem in structured steps so you learn the method, not just the answer.",
  },
  quiz: {
    icon: ListChecks,
    label: "AI Quiz builder",
    endpoint: "/coach/quiz",
    method: "post",
    desc: "Generate a fresh multi-choice quiz on any topic. Grade it yourself right here.",
  },
  exam: {
    icon: Calendar,
    label: "AI Exam prep plan",
    endpoint: "/coach/exam-plan",
    method: "post",
    desc: "Build a day-by-day study plan for the topics + timeline you provide.",
  },
};

export default function CoachToolModal({ open, onClose, type, defaults = {} }) {
  const meta = TOOL_META[type];
  const [phase, setPhase] = useState("form"); // form | loading | result | locked
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizResults, setShowQuizResults] = useState(false);

  // Reset when opened
  useEffect(() => {
    if (!open) return;
    setPhase(type === "explain" ? "loading" : "form");
    setResult(null);
    setForm({
      // per-type defaults
      ...(type === "quiz"
        ? { subject: "biology", topic: "", difficulty: "medium", count: 5 }
        : {}),
      ...(type === "exam"
        ? { subject: "biology", topics: "", days: 7, hours_per_day: 2 }
        : {}),
      ...(type === "step" ? { problem: "", subject: "" } : {}),
      ...defaults,
    });
    setQuizAnswers({});
    setShowQuizResults(false);
    // Explain is fully auto — fire immediately if we have the inputs.
    if (type === "explain") {
      const seed = { ...defaults };
      if (seed.question && seed.correct_answer) {
        run(seed);
      } else {
        setPhase("form");
      }
    }
    // Cache-backed quiz (Flashcards picker → "Generate more"): auto-run so
    // the user sees cards without touching a form. They can still hit
    // "Regenerate fresh set" on the results screen.
    if (type === "quiz" && defaults?.use_cache && defaults?.subject && defaults?.topic) {
      run({ ...defaults, use_cache: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type]);

  const run = async (payload) => {
    setPhase("loading");
    try {
      const body =
        type === "exam"
          ? {
              subject: payload.subject,
              topics: (payload.topics || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
              days: Number(payload.days) || 7,
              hours_per_day: Number(payload.hours_per_day) || 2,
            }
          : type === "quiz"
          ? {
              subject: payload.subject,
              topic: payload.topic || null,
              difficulty: payload.difficulty,
              count: Number(payload.count) || 5,
              use_cache: !!payload.use_cache,
              regenerate: !!payload.regenerate,
            }
          : payload;
      const r = await api.post(meta.endpoint, body);
      setResult(r.data);
      setPhase("result");
      window.dispatchEvent(new Event("pm-billing-refresh"));
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && d?.code === "pro_only") {
        setResult({ upgrade_message: d.message });
        setPhase("locked");
      } else if (e?.response?.status === 402) {
        // Out-of-credits: same locked screen but with the credits CTA instead
        // of the Pro CTA. Handles both {reason} and plain-string details.
        const reason =
          (d && typeof d === "object" && (d.reason || d.message)) ||
          (typeof d === "string" ? d : null) ||
          "You're out of AI credits. Buy a top-up pack or upgrade to keep generating.";
        setResult({ upgrade_message: reason, out_of_credits: true });
        setPhase("locked");
      } else {
        toast.error(typeof d === "string" ? d : "Couldn't run the AI tool. Try again?");
        setPhase("form");
      }
    }
  };

  const regenerateQuiz = () => {
    // Force a fresh GPT call and overwrite the cached set.
    run({ ...form, use_cache: true, regenerate: true });
    setQuizAnswers({});
    setShowQuizResults(false);
  };

  if (!open) return null;
  const Icon = meta.icon;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/40"
        onClick={onClose}
        data-testid="coach-tool-backdrop"
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92vw] max-w-2xl max-h-[86vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: "var(--paper)", border: "1px solid var(--border)" }}
        data-testid={`coach-tool-modal-${type}`}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: "var(--border)" }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg,#2A5CE0,#7B4DFF)" }}
            >
              <Icon size={16} color="white" />
            </div>
            <div>
              <div className="font-display font-bold text-sm flex items-center gap-1.5">
                {meta.label}
                <span
                  className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                  style={{ background: "#c98a3b22", color: "#c98a3b" }}
                >
                  PRO
                </span>
              </div>
              <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                Powered by Gemini 2.5 Flash · 0.25 credit / call
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--muted)]"
            data-testid="coach-tool-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {phase === "locked" && (
            <div className="text-center py-6" data-testid="coach-tool-locked">
              <div
                className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center"
                style={{ background: "#c98a3b22" }}
              >
                <Crown size={24} style={{ color: "#c98a3b" }} />
              </div>
              <div className="font-display font-bold mb-1">
                {result?.out_of_credits ? "Out of credits" : "Pro-only feature"}
              </div>
              <p
                className="text-sm mb-4 max-w-md mx-auto"
                style={{ color: "var(--text-secondary)" }}
              >
                {result?.upgrade_message}
              </p>
              {result?.out_of_credits ? (
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <Link
                    to="/app/plans#credit-packs"
                    onClick={onClose}
                    className="pm-btn-primary inline-flex items-center gap-1.5 text-sm"
                    data-testid="coach-tool-buy-credits"
                  >
                    <Sparkles size={14} /> Buy top-up credits
                  </Link>
                  <Link
                    to="/app/plans"
                    onClick={onClose}
                    className="pm-btn-secondary inline-flex items-center gap-1.5 text-sm"
                    data-testid="coach-tool-upgrade"
                  >
                    <Crown size={14} /> Upgrade plan
                  </Link>
                </div>
              ) : (
                <Link
                  to="/app/plans"
                  onClick={onClose}
                  className="pm-btn-primary inline-flex items-center gap-1.5 text-sm"
                  data-testid="coach-tool-upgrade"
                >
                  <Crown size={14} /> Upgrade to Pro
                </Link>
              )}
            </div>
          )}

          {phase === "loading" && (
            <div
              className="flex flex-col items-center gap-3 py-10"
              data-testid="coach-tool-loading"
            >
              <Loader2 className="animate-spin" size={22} />
              <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
                Coach is thinking…
              </div>
            </div>
          )}

          {phase === "form" && type === "step" && (
            <StepForm form={form} setForm={setForm} onSubmit={() => run(form)} />
          )}
          {phase === "form" && type === "quiz" && (
            <QuizForm form={form} setForm={setForm} onSubmit={() => run(form)} />
          )}
          {phase === "form" && type === "exam" && (
            <ExamForm form={form} setForm={setForm} onSubmit={() => run(form)} />
          )}
          {phase === "form" && type === "explain" && (
            <div
              className="text-sm text-center py-6"
              style={{ color: "var(--text-secondary)" }}
            >
              Explain-answer needs a question + your answer + the correct
              answer. Trigger this from a question review screen.
            </div>
          )}

          {phase === "result" && type === "explain" && (
            <ExplainResult result={result} />
          )}
          {phase === "result" && type === "step" && <StepResult result={result} />}
          {phase === "result" && type === "quiz" && (
            <QuizResult
              result={result}
              answers={quizAnswers}
              setAnswers={setQuizAnswers}
              showResults={showQuizResults}
              onGrade={() => setShowQuizResults(true)}
              onRegenerate={regenerateQuiz}
              onReset={() => {
                setPhase("form");
                setResult(null);
                setQuizAnswers({});
                setShowQuizResults(false);
              }}
            />
          )}
          {phase === "result" && type === "exam" && <ExamResult result={result} />}
        </div>

        {/* Footer */}
        {phase === "result" && type !== "quiz" && (
          <div
            className="p-3 border-t flex justify-end gap-2"
            style={{ borderColor: "var(--border)" }}
          >
            <button
              onClick={() => {
                setPhase("form");
                setResult(null);
              }}
              className="pm-btn-secondary text-xs"
              data-testid="coach-tool-again"
            >
              Run again
            </button>
            <button
              onClick={onClose}
              className="pm-btn-primary text-xs"
              data-testid="coach-tool-done"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ---------- Sub-forms ----------
function StepForm({ form, setForm, onSubmit }) {
  return (
    <div className="space-y-3" data-testid="coach-tool-form-step">
      <div>
        <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
          The problem
        </label>
        <textarea
          value={form.problem || ""}
          onChange={(e) => setForm({ ...form, problem: e.target.value })}
          rows={4}
          placeholder="Paste the problem — e.g. 'Solve 2x + 6 = 14' or a full physics word problem."
          className="pm-input w-full mt-1 text-sm"
          data-testid="coach-tool-problem-input"
        />
      </div>
      <div>
        <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
          Subject (optional)
        </label>
        <input
          value={form.subject || ""}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          placeholder="math · physics · chem · biochem · orgo"
          className="pm-input w-full mt-1 text-sm"
        />
      </div>
      <button
        onClick={onSubmit}
        disabled={!(form.problem || "").trim()}
        className="pm-btn-primary w-full text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        data-testid="coach-tool-submit"
      >
        <Sparkles size={14} /> Solve step-by-step
      </button>
    </div>
  );
}

function QuizForm({ form, setForm, onSubmit }) {
  return (
    <div className="space-y-3" data-testid="coach-tool-form-quiz">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
            Subject
          </label>
          <input
            value={form.subject || ""}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="pm-input w-full mt-1 text-sm"
            data-testid="coach-tool-subject-input"
          />
        </div>
        <div>
          <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
            Topic (optional)
          </label>
          <input
            value={form.topic || ""}
            onChange={(e) => setForm({ ...form, topic: e.target.value })}
            placeholder="e.g. cell division"
            className="pm-input w-full mt-1 text-sm"
            data-testid="coach-tool-topic-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
            Difficulty
          </label>
          <select
            value={form.difficulty || "medium"}
            onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
            className="pm-input w-full mt-1 text-sm"
          >
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
            # Questions
          </label>
          <input
            type="number"
            min={3}
            max={20}
            value={form.count || 5}
            onChange={(e) => setForm({ ...form, count: e.target.value })}
            className="pm-input w-full mt-1 text-sm"
          />
        </div>
      </div>
      <button
        onClick={onSubmit}
        disabled={!(form.subject || "").trim()}
        className="pm-btn-primary w-full text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        data-testid="coach-tool-submit"
      >
        <Sparkles size={14} /> Generate quiz
      </button>
    </div>
  );
}

function ExamForm({ form, setForm, onSubmit }) {
  return (
    <div className="space-y-3" data-testid="coach-tool-form-exam">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
            Subject
          </label>
          <input
            value={form.subject || ""}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="pm-input w-full mt-1 text-sm"
            data-testid="coach-tool-subject-input"
          />
        </div>
        <div>
          <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
            Days until exam
          </label>
          <input
            type="number"
            min={1}
            max={60}
            value={form.days || 7}
            onChange={(e) => setForm({ ...form, days: e.target.value })}
            className="pm-input w-full mt-1 text-sm"
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
          Topics (comma-separated)
        </label>
        <input
          value={form.topics || ""}
          onChange={(e) => setForm({ ...form, topics: e.target.value })}
          placeholder="e.g. photosynthesis, enzyme kinetics, cell division"
          className="pm-input w-full mt-1 text-sm"
        />
      </div>
      <div>
        <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
          Hours per day
        </label>
        <input
          type="number"
          min={0.5}
          max={12}
          step={0.5}
          value={form.hours_per_day || 2}
          onChange={(e) => setForm({ ...form, hours_per_day: e.target.value })}
          className="pm-input w-full mt-1 text-sm"
        />
      </div>
      <button
        onClick={onSubmit}
        disabled={!(form.subject || "").trim()}
        className="pm-btn-primary w-full text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        data-testid="coach-tool-submit"
      >
        <Sparkles size={14} /> Build exam plan
      </button>
    </div>
  );
}

// ---------- Sub-results ----------
function ExplainResult({ result }) {
  return (
    <div data-testid="coach-tool-result-explain">
      <div
        className="pm-badge mb-3 inline-flex"
        style={{
          background: result.was_correct ? "#4f794222" : "#c95e5e22",
          color: result.was_correct ? "#4f7942" : "#c95e5e",
        }}
      >
        {result.was_correct ? (
          <>
            <CheckCircle2 size={12} className="mr-1.5" /> You were correct
          </>
        ) : (
          <>
            <XCircle size={12} className="mr-1.5" /> You missed this one
          </>
        )}
      </div>
      <p
        className="text-sm leading-relaxed whitespace-pre-wrap"
        style={{ color: "var(--text)" }}
      >
        {result.explanation}
      </p>
    </div>
  );
}

function StepResult({ result }) {
  const steps = result?.steps || [];
  if (!steps.length && result?.raw) {
    return (
      <pre
        className="text-xs whitespace-pre-wrap"
        style={{ color: "var(--text-secondary)" }}
      >
        {result.raw}
      </pre>
    );
  }
  return (
    <div className="space-y-3" data-testid="coach-tool-result-step">
      {steps.map((s, i) => (
        <div
          key={i}
          className="pm-card p-3"
          data-testid={`coach-tool-step-${i}`}
          style={{ borderLeft: "3px solid var(--primary)" }}
        >
          <div className="font-semibold text-sm mb-1">{s.title}</div>
          <p
            className="text-sm leading-snug"
            style={{ color: "var(--text-secondary)" }}
          >
            {s.detail}
          </p>
        </div>
      ))}
      {result.final_answer && (
        <div
          className="pm-card p-3"
          style={{ background: "#4f794215", borderLeft: "3px solid #4f7942" }}
          data-testid="coach-tool-step-final"
        >
          <div className="text-xs font-semibold uppercase mb-1" style={{ color: "#4f7942" }}>
            Final Answer
          </div>
          <div className="font-display font-bold">{result.final_answer}</div>
          {result.why_it_works && (
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              {result.why_it_works}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function QuizResult({ result, answers, setAnswers, showResults, onGrade, onReset, onRegenerate }) {
  const qs = result?.questions || [];
  const graded = showResults
    ? qs.reduce(
        (acc, q, i) => acc + (answers[i] === q.correct ? 1 : 0),
        0
      )
    : 0;
  const isDone = Object.keys(answers).length === qs.length && qs.length > 0;
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [savedDeckId, setSavedDeckId] = useState(null);

  const saveAsDeck = async () => {
    setSaveState("saving");
    try {
      // Turn each MCQ into a flashcard: front = question, back = correct answer + why.
      const cards = qs.map((q, i) => {
        const correctChoice = (q.choices || []).find(
          (c) => c.trim().charAt(0) === q.correct
        ) || `${q.correct}`;
        return {
          front: `${i + 1}. ${q.question}`,
          back: `Correct: ${correctChoice}\n\nWhy: ${q.explanation || "(no explanation)"}`,
          meta: { correct: q.correct, choices: q.choices || [] },
        };
      });
      const titleBits = [result.subject, result.topic].filter(Boolean).join(" · ");
      const r = await api.post("/custom-flashcards/save-chart", {
        source_chart: `ai-quiz-${result.quiz_id || Date.now()}`,
        title: `AI Quiz: ${titleBits || "Practice"}`,
        cards,
      });
      setSavedDeckId(r.data.id);
      setSaveState("saved");
      toast.success(`Saved ${cards.length} cards to My Decks.`);
    } catch (e) {
      toast.error("Couldn't save the deck. Try again?");
      setSaveState("idle");
    }
  };

  return (
    <div className="space-y-4" data-testid="coach-tool-result-quiz">
      {result?.cached && (
        <div
          className="pm-card p-3 flex items-center justify-between gap-3"
          style={{ background: "#4f794215", borderLeft: "3px solid #4f7942" }}
          data-testid="coach-tool-quiz-cached-banner"
        >
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            <b style={{ color: "#4f7942" }}>Loaded from library</b> — this set
            was previously generated for the same topic. Save credits by reusing
            it, or generate a fresh one.
          </div>
          {onRegenerate && (
            <button
              onClick={onRegenerate}
              className="pm-btn-secondary text-xs inline-flex items-center gap-1 shrink-0"
              data-testid="coach-tool-quiz-regenerate"
              title="Force a fresh GPT-generated set (0.25 credit)"
            >
              <Sparkles size={12} /> Regenerate fresh set
            </button>
          )}
        </div>
      )}
      <div className="text-xs" style={{ color: "var(--text-muted)" }}>
        {qs.length} questions · {result.difficulty} · {result.subject}
        {result.topic ? ` · ${result.topic}` : ""}
      </div>
      {qs.map((q, i) => (
        <div key={i} className="pm-card p-3" data-testid={`coach-tool-quiz-q-${i}`}>
          <div className="font-semibold text-sm mb-2">
            {i + 1}. {q.question}
          </div>
          <div className="space-y-1">
            {(q.choices || []).map((choice) => {
              const letter = choice.trim().charAt(0);
              const selected = answers[i] === letter;
              const isCorrect = showResults && letter === q.correct;
              const isWrong = showResults && selected && letter !== q.correct;
              return (
                <button
                  key={choice}
                  onClick={() =>
                    !showResults &&
                    setAnswers({ ...answers, [i]: letter })
                  }
                  disabled={showResults}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm border"
                  style={{
                    borderColor: isCorrect
                      ? "#4f7942"
                      : isWrong
                      ? "#c95e5e"
                      : selected
                      ? "var(--primary)"
                      : "var(--border)",
                    background: isCorrect
                      ? "#4f794215"
                      : isWrong
                      ? "#c95e5e15"
                      : selected
                      ? "var(--muted)"
                      : "var(--paper)",
                    color: "var(--text)",
                  }}
                >
                  {choice}
                </button>
              );
            })}
          </div>
          {showResults && (
            <p
              className="text-xs mt-2"
              style={{ color: "var(--text-secondary)" }}
            >
              <b>Why:</b> {q.explanation}
            </p>
          )}
        </div>
      ))}
      <div className="flex items-center gap-2 justify-end flex-wrap">
        {saveState === "saved" && savedDeckId ? (
          <Link
            to={`/app/decks/${savedDeckId}`}
            className="pm-btn-secondary text-sm inline-flex items-center gap-1.5"
            data-testid="coach-tool-quiz-view-deck"
          >
            <BookmarkCheck size={14} /> View in My Decks
          </Link>
        ) : (
          <button
            onClick={saveAsDeck}
            disabled={saveState === "saving" || qs.length === 0}
            className="pm-btn-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="coach-tool-quiz-save-deck"
            title="Save this quiz as a spaced-repetition flashcard deck"
          >
            {saveState === "saving" ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Bookmark size={14} />
            )}
            {saveState === "saving" ? "Saving…" : `Save as ${qs.length} flashcards`}
          </button>
        )}
        {!showResults ? (
          <button
            onClick={onGrade}
            disabled={!isDone}
            className="pm-btn-primary text-sm disabled:opacity-50"
            data-testid="coach-tool-quiz-grade"
          >
            Grade my quiz
          </button>
        ) : (
          <>
            <div
              className="text-sm font-semibold"
              data-testid="coach-tool-quiz-score"
            >
              Score: {graded}/{qs.length}
            </div>
            <button
              onClick={onReset}
              className="pm-btn-secondary text-sm"
              data-testid="coach-tool-quiz-reset"
            >
              Another quiz
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ExamResult({ result }) {
  const days = result?.days || [];
  return (
    <div className="space-y-3" data-testid="coach-tool-result-exam">
      {result.overview && (
        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
          {result.overview}
        </p>
      )}
      {days.map((d, i) => (
        <div key={i} className="pm-card p-3" data-testid={`coach-tool-exam-day-${d.day || i + 1}`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-semibold text-sm">
              Day {d.day || i + 1}: {d.focus}
            </div>
          </div>
          <ul className="space-y-1 text-sm">
            {(d.activities || []).map((a, j) => (
              <li
                key={j}
                className="inline-flex items-start gap-1.5"
                style={{ color: "var(--text-secondary)" }}
              >
                <ChevronRight
                  size={12}
                  className="mt-1 flex-shrink-0"
                  style={{ color: "var(--primary)" }}
                />
                <span>{a}</span>
              </li>
            ))}
          </ul>
          {d.check_yourself && (
            <p
              className="text-xs mt-2 italic"
              style={{ color: "var(--text-muted)" }}
            >
              🎯 Check yourself: {d.check_yourself}
            </p>
          )}
        </div>
      ))}
      {result.final_prep_tip && (
        <div
          className="pm-card p-3"
          style={{ background: "#c98a3b15", borderLeft: "3px solid #c98a3b" }}
        >
          <div className="text-xs font-semibold uppercase mb-1" style={{ color: "#c98a3b" }}>
            Day before the exam
          </div>
          <p className="text-sm" style={{ color: "var(--text)" }}>
            {result.final_prep_tip}
          </p>
        </div>
      )}
    </div>
  );
}
